import { EventFormatterService } from '@addins/core/core';
import { Injectable } from '@angular/core';
import { GuidTools } from '@models/guid';
import { CallCard } from '@models/imported/SagaSchema/CallCard';
import { UnitActivity } from '@models/imported/SagaSchema/UnitActivity';
import { ListResourceStatus } from '@models/imported/SagaStateLib/ListResourceStatus';
import { TranslateService } from '@ngx-translate/core';
import { CacheService, CacheState } from '@services/cache/cache.service';
import { FetchListService } from '@services/fetchlist/fetchlist.service';
import { SagaSettingsService } from '@services/settings';
import { MyEquipmentService } from '@techwan/ionic-core';
import { Subscription } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { IHeaderTitle, IHeaderTitleProvider } from '../header-title-provider/header-title-provider.service';

@Injectable({
  providedIn: 'root'
})
export class DefaultHeaderTitleProviderService implements IHeaderTitleProvider {
  private myEquipmentSub: Subscription = null;

  private _myUnit: UnitActivity = null;
  protected get myUnit(): UnitActivity {
    return this._myUnit;
  }

  private myUnitChangedSub: Subscription = null;

  protected myCallcard: CallCard = null;
  protected myCallcardChangedSub: Subscription = null;

  private headerTitle: IHeaderTitle = { unitName: '', unitStatus: '' };
  private headerFormat: string = null;

  constructor(
    private myEquipmentService: MyEquipmentService,
    private fetchList: FetchListService,
    protected cacheService: CacheService,
    private translate: TranslateService,
    private eventFormatter: EventFormatterService,
    private settingsService: SagaSettingsService
  ) {}

  setup() {
    this.myEquipmentSub = this.myEquipmentService.$myUnit.subscribe(myUnit => this.onMyEquimentChanged(myUnit));
  }

  private onMyEquimentChanged(myUnit) {
    this.clearHeaderTitle();

    if (!myUnit) {
      this.onUnitDestroyed();
    } else {
      this.headerTitle.unitName = myUnit.Name;

      this.fetchList.$ready
        .pipe(
          filter(isReady => isReady),
          first()
        )
        .subscribe(() => {
          const statusResource: ListResourceStatus = this.fetchList.getRow(ListResourceStatus, myUnit.Status);
          if (statusResource != null) {
            this.updateUnitStatus(statusResource);
          }

          this.cacheService.state
            .pipe(
              filter(state => state === CacheState.ready),
              first()
            )
            .subscribe(() => {
              this.headerFormat = this.settingsService.getValue('SagaMobileWebClient.HeaderTitle.FormatEnum');

              this._myUnit = this.cacheService.getObjectByUid(myUnit.ObjGuid);
              this.myUnitChangedSub = this.myUnit.$changed.subscribe(() => this.onUnitUpdate());
              this.onUnitUpdate();
            });
        });
    }
  }

  private clearHeaderTitle() {
    this.headerTitle.unitName = '';
    this.headerTitle.unitStatus = '';
  }

  protected onUnitDestroyed() {
    this.myCallcardChangedSub?.unsubscribe();
    this.myCallcardChangedSub = null;
    this.myCallcard = null;

    this.myUnitChangedSub?.unsubscribe();
    this.myUnitChangedSub = null;
    this._myUnit = null;

    this.clearHeaderTitle();
  }

  private onUnitUpdate() {
    this.updateCallCard();

    this.clearHeaderTitle();
    this.headerTitle.unitName = this.myUnit.Name;

    this.updateUnitStatus();
  }

  protected updateUnitStatus(statusResource: ListResourceStatus = this.myUnit.status) {
    const statusName: string = this.translate.instant(`Mobile.${statusResource.Name}`);
    const extraInfo: string = this.headerFormat !== 'TitleHidden' ? this.getStatusExtraInfo() : null;
    const unitStatus: string = [statusName, extraInfo].filter(name => name).join(' - ');

    this.headerTitle.unitStatus = unitStatus;
  }

  protected getStatusExtraInfo(): string {
    return this.myCallcard ? this.eventFormatter.format(this.myCallcard.event, true) : null;
  }

  protected updateCallCard() {
    if (GuidTools.isValid(this.myUnit.CallCardID)) {
      const isDifferentCallcard: boolean = this.myCallcard === null || this.myCallcard.ObjGuid !== this.myUnit.CallCardID;
      if (isDifferentCallcard) {
        this.myCallcard = this.cacheService.getObjectByUid(this.myUnit.CallCardID);
        this.myCallcardChangedSub?.unsubscribe();
        this.myCallcardChangedSub = this.cacheService
          .listenForChange(CallCard)
          .pipe(filter(cacheEvent => (cacheEvent.object as CallCard).ObjGuid === this.myCallcard.ObjGuid))
          .subscribe(() => this.updateUnitStatus());
      }
    } else {
      this.myCallcardChangedSub?.unsubscribe();
      this.myCallcard = null;
    }
  }

  cleanup() {
    this.myEquipmentSub?.unsubscribe();
    this.onUnitDestroyed();
  }

  getHeaderTitle(): IHeaderTitle {
    return this.headerTitle;
  }
}
